import {BaseElement, BaseElementAsync, OSkinableInit} from "back/commons/basis";
import {Button} from "back/commons/widgets/buttons";
import {POPUP} from "back/commons/widgets/popups";
import {IReg, IUiEnv, REG} from "lib/commons/registry";
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {JSrcFields, RESPS} from "lib/wsp/src";
import {IWspEnv, WSP, Wsp} from "lib/wsp/wsp";
import {ITEM} from "lib/wsp/item";
import {IDatasResp} from "lib/wsp/wspMetaUi";
import {InputUserPanel, OInputUserInit} from "back/core/widgets/inputs";
import {EUserType, JUser} from "lib/core/user";
import {ERROR} from "lib/core/errorReport";


/**
 * Dialogue d'affectation d'une responsabilité sur un ou plusieurs items
 */

export interface SrcEditResp extends BaseElement {
	initialize(init: OSrcEditRespInit): this
}

export interface OSrcEditRespInit extends OSkinableInit {
	shortDescs: JSrcFields[],
	resp: IDatasResp,
	doBtnLabel?: string
	confInput?: OInputUserInit<any>,
}

export class SrcEditResp extends BaseElementAsync {

	/** Contexte */
	reg: IReg<IWspEnv & IUiEnv>;

	longDescs: JSrcFields[];

	protected _inptElt: InputUserPanel<any>;
	protected _doBtn: Button;

	private _iniValues: Set<string>;

	get wsp(): Wsp {return this.reg.env.wsp};

	conf: OSrcEditRespInit;

	protected async _initialize(init: OSrcEditRespInit) {
		this.reg = this.findReg(init);
		this.conf = init;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("webzone:panel", sr);
		this.reg.installSkin("standard-dialog", sr);
		this._initAndInstallSkin(this.localName, init);

		const refUris: string[] = [];
		this.conf.shortDescs.forEach((entry) => refUris.push(entry.srcUri))
		this.longDescs = await WSP.fetchLongDescs(this.wsp, this, ...refUris);

		this._iniValues = new Set();
		this.longDescs.forEach((shortDesc) => {
			const respList = RESPS.toRespsList(shortDesc.rspUsrs);
			if (respList && respList[this.conf.resp.code])
				respList[this.conf.resp.code].forEach((account) => this._iniValues.add(account))
		});

		const main = sr.appendChild(<div id="main">
			<div class="key">{this.conf.resp.name}</div>
		</div>);
		this._inptElt = main.appendChild(<InputUserPanel î={Object.assign({
			reg: this.reg,
			userCard: this.conf.resp.userCard,
			required: false,
			selectWithConfirm: false,
			usersGridInit: {
				usersSrv: init.reg.env.universe.useUsers,
				filterGroups: this.conf.resp.usersSelection.restrictFromGroups,
				filterType: this.conf.resp.usersSelection.userType === 'groups' ? EUserType.group : (this.conf.resp.usersSelection.userType === 'users' ? EUserType.user : null),
			}
		} as OInputUserInit<any>, this.conf.confInput)}/> as InputUserPanel<any>) as InputUserPanel<any>;
		if (this.longDescs.length === 1) {
			this._inptElt.value = Array.from(this._iniValues);
		} else {
			if (this.conf.resp.userCard === "single") {
				// On ne met la valeur initiale QUE si elle est homogène
				if (this._iniValues.size === 1)
					this._inptElt.value = Array.from(this._iniValues);
				else if (this._iniValues.size > 1)
					this._inptElt.forcedIniUsers = [{account: '-', nickNames: ['Valeurs hétérogènes'], isUnknown: true} as JUser];
			} else {
				const iniUsers = this.reg.env.universe.useUsers.getUserSet(Array.from(this._iniValues), true);
				iniUsers.then((resp) => {
					this._inptElt.forcedIniUsers = resp;
				})
			}
		}

		this._inptElt.addEventListener("input", async () => {
			this.refreshUi();
		});

		const footer = sr.appendChild(<div id="footer"/>);
		this._doBtn = footer.appendChild(<Button id="do" disabled class="default" label={this.conf.doBtnLabel || 'Enregistrer'} ui-context="dialog" onclick={this.onDoBtn}/>) as Button;
		footer.appendChild(<Button id="cancel" label="Annuler" ui-context="dialog" onclick={this.onCancelBtn}/>);

		this._inptElt.focus();
	}

	private refreshUi() {
		const isDirty = this.computeDiffs() != null;
		DOM.setAttrBool(this._doBtn, 'disabled', !isDirty);
	}

	private async validAndDo() {
		try {
			const diffs: { adds?: string[], removes?: string[] } | { single: string } | null = this.computeDiffs();
			if (diffs) {
				const srcUris: string[] = [];
				this.longDescs.forEach((shortDesc) => srcUris.push(shortDesc.srcUri));
				if ("single" in diffs)
					await ITEM.fetchUpdateRespByRespSingle(this.reg.env.wsp, this, srcUris, this.conf.resp.code, (diffs as any).single);
				else
					await ITEM.fetchUpdateRespByRespMulti(this.reg.env.wsp, this, srcUris, this.conf.resp.code, (diffs as any).adds, (diffs as any).removes);
				POPUP.findPopupableParent(this).close(true);
			}
			POPUP.findPopupableParent(this).close(false);
		} catch (e) {
			const resp = this.conf.resp.name;
			await ERROR.report(`Échec lors de la mise à jour de la responsabilité '${resp}'`, e);
			POPUP.findPopupableParent(this).close(null);
		}
	}

	private onCancelBtn(this: Button) {
		POPUP.findPopupableParent(this).close();
	}

	private onDoBtn(this: Button) {
		if (!this.disabled)
			(DOMSH.findHost(this) as SrcEditResp).validAndDo();
	}

	/**
	 * Calcul les directives d'affectation des reps
	 * @return null si aucune action à exécuter
	 * @private
	 */
	private computeDiffs(): { adds?: string[], removes?: string[] } | { single: string } | null {
		const result: { adds?: string[], removes?: string[], single?: string } = {};

		const srcUris: string[] = [];
		this.longDescs.forEach((shortDesc) => srcUris.push(shortDesc.srcUri));
		if (this.conf.resp.userCard === 'single') {
			if (this._inptElt.value && this._inptElt.value[0]) {
				if (!this._iniValues || this._iniValues.size != 1 || !this._iniValues.has(this._inptElt.value[0])) {
					result.single = this._inptElt.value[0];
					return result;
				}
			} else if (this._iniValues && this._iniValues.size > 0 && (!this._inptElt.forcedIniUsers || this._inptElt.forcedIniUsers.length == 0)) {
				result.removes = Array.from(this._iniValues);
				return result;
			}
		} else if (this.conf.resp.userCard === 'multi') {
			result.adds = [];
			result.removes = [];
			// Recherche des valeurs supprimées
			if (this._iniValues) {
				this._iniValues.forEach((account) => {
					if (!this._inptElt.forcedIniUsers?.find((user) => user.account === account) && !this._inptElt.value?.find((val) => account === val))
						result.removes.push(account);
				})
			}
			// Valeurs ajoutées
			this._inptElt.value?.forEach((account) => {
				result.adds.push(account);
			});
			if (result.adds.length > 0 || result.removes.length > 0)
				return result;
		}
		return null;
	}
}

REG.reg.registerSkin('wsp-src-editresp', 1, /* language=CSS */ `
	:focus-visible {
		outline: var(--focus-outline);
	}

	.key {
		margin-inline-end: 1em;
	}

	#main {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: row;
		align-items: center;
		padding: .5em;
	}

	c-input-users-panel {
		flex: 1;
	}

	c-button {
		min-width: 8em;
	}
`);

customElements.define("wsp-src-editresp", SrcEditResp);
